import React, { useState } from "react";
import { Button, Chip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useUserAuth } from "../context/UserAuthContext";
import { FUNCTIONS } from "../config";
import useIsMobile from "../hooks/useIsMobile";

const PLANS = [
    {
        id: "free",
        name: "Free",
        price: "$0",
        period: "/month",
        description: "Get started with basic quoting",
        quota: "25 quotes / month",
        features: [
            "25 quotes per month",
            "1 agent",
            "Slack & HubSpot integration",
            "Basic analytics",
        ],
    },
    {
        id: "unlimited",
        name: "Unlimited",
        price: "$100",
        period: "/month",
        description: "Unlimited quoting for growing teams",
        quota: "Unlimited quotes",
        features: [
            "Unlimited quotes",
            "Unlimited agents",
            "Slack & HubSpot integration",
            "Advanced analytics",
            "Priority support",
        ],
    },
];

const Billing = () => {
    const { user, orgId, orgData, isAdmin } = useUserAuth();
    const [loading, setLoading] = useState(false);
    const [testMode, setTestMode] = useState(false);
    const isMobile = useIsMobile();
    const currentPlan = orgData?.plan || "free";
    const quotaLimit = orgData?.monthlyQuoteLimit ?? null;

    // Compute quotes used this month from all agents' quoteLog entries
    const quotesUsed = (() => {
        const agents = orgData?.agents || {};
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const toDate = (ts) => {
            if (ts && typeof ts.toDate === "function") return ts.toDate();
            if (ts && ts.seconds) return new Date(ts.seconds * 1000);
            return new Date(ts);
        };
        let count = 0;
        Object.values(agents).forEach((agent) => {
            (agent.quoteLog || []).forEach((entry) => {
                const d = toDate(entry.date);
                if (!isNaN(d) && d >= monthStart) count++;
            });
        });
        return count;
    })();

    const handleUpgrade = async () => {
        if (!orgId) return;
        setLoading(true);
        try {
            const token = await user.getIdToken();
            const res = await fetch(FUNCTIONS.stripeCheckout, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({
                    orgId,
                    testMode,
                    successUrl: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
                    cancelUrl: `${window.location.origin}/billing`,
                }),
            });
            const data = await res.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                console.error("Stripe checkout error:", data.error);
            }
        } catch (err) {
            console.error("Stripe checkout error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleManage = async () => {
        if (!orgId) return;
        setLoading(true);
        try {
            const token = await user.getIdToken();
            const res = await fetch(FUNCTIONS.stripePortal, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({
                    orgId,
                    testMode,
                    returnUrl: `${window.location.origin}/billing`,
                }),
            });
            const data = await res.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                console.error("Stripe portal error:", data.error);
            }
        } catch (err) {
            console.error("Stripe portal error:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <h1 style={{ fontSize: "24px", fontWeight: 600, color: "#1a1a1a", margin: 0 }}>Billing</h1>
                    <Chip
                        label={currentPlan === "free" ? "Free Plan" : "Unlimited"}
                        size="small"
                        sx={{
                            backgroundColor: currentPlan === "free" ? "#f3f4f6" : "#f0ebfc",
                            color: currentPlan === "free" ? "#6b7280" : "#825ee7",
                            fontWeight: 600,
                            fontSize: "11px",
                        }}
                    />
                </div>
            </div>

            {/* Usage */}
            <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: "24px", marginBottom: "24px" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "12px" }}>
                    Current Usage
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: "8px", marginBottom: "8px" }}>
                    <span style={{ fontSize: "32px", fontWeight: 700, color: "#1a1a1a" }}>{quotesUsed}</span>
                    <span style={{ fontSize: "14px", color: "#6b7280" }}>
                        {quotaLimit ? `/ ${quotaLimit} quotes this month` : "quotes this month"}
                    </span>
                </div>
                {quotaLimit && (
                    <div style={{ height: "6px", backgroundColor: "#f3f4f6", borderRadius: "3px", overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${Math.min((quotesUsed / quotaLimit) * 100, 100)}%`,
                            backgroundColor: quotesUsed >= quotaLimit ? "#dc2626" : "#825ee7",
                            borderRadius: "3px",
                            transition: "width 0.3s",
                        }} />
                    </div>
                )}
                {quotaLimit && quotesUsed >= quotaLimit && (
                    <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "8px", marginBottom: 0 }}>
                        You've reached your monthly limit. Upgrade to continue creating quotes.
                    </p>
                )}
            </div>

            {/* Plans */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "16px" }}>
                {PLANS.map((plan) => {
                    const isCurrent = plan.id === currentPlan;
                    const isUpgrade = plan.id === "unlimited" && currentPlan === "free";
                    return (
                        <div
                            key={plan.id}
                            style={{
                                backgroundColor: "#fff",
                                borderRadius: "12px",
                                border: isCurrent ? "2px solid #825ee7" : "1px solid #e5e7eb",
                                padding: isMobile ? "20px" : "32px",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                                <h3 style={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", margin: 0 }}>{plan.name}</h3>
                                {isCurrent && <Chip label="Current" size="small" sx={{ backgroundColor: "#f0ebfc", color: "#825ee7", fontWeight: 600, fontSize: "10px", height: "20px" }} />}
                            </div>
                            <p style={{ color: "#6b7280", fontSize: "13px", margin: "0 0 16px 0" }}>{plan.description}</p>

                            <div style={{ marginBottom: "20px" }}>
                                <span style={{ fontSize: "36px", fontWeight: 700, color: "#1a1a1a" }}>{plan.price}</span>
                                <span style={{ fontSize: "14px", color: "#6b7280" }}>{plan.period}</span>
                            </div>

                            <div style={{ flex: 1, marginBottom: "24px" }}>
                                {plan.features.map((feature) => (
                                    <div key={feature} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                                        <CheckCircleIcon style={{ fontSize: "16px", color: "#825ee7" }} />
                                        <span style={{ fontSize: "13px", color: "#374151" }}>{feature}</span>
                                    </div>
                                ))}
                            </div>

                            {isAdmin && isUpgrade && (
                                <Button
                                    variant="contained"
                                    onClick={handleUpgrade}
                                    disabled={loading}
                                    fullWidth
                                    style={{
                                        backgroundColor: "#825ee7",
                                        color: "#fff",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        borderRadius: "8px",
                                        padding: "10px",
                                        fontSize: "14px",
                                    }}
                                >
                                    {loading ? "Redirecting..." : "Upgrade to Unlimited"}
                                </Button>
                            )}
                            {isAdmin && isCurrent && currentPlan === "unlimited" && (
                                <Button
                                    variant="outlined"
                                    onClick={handleManage}
                                    disabled={loading}
                                    fullWidth
                                    sx={{
                                        color: "#6b7280",
                                        borderColor: "#d1d5db",
                                        textTransform: "none",
                                        fontWeight: 500,
                                        borderRadius: "8px",
                                        padding: "10px",
                                        fontSize: "14px",
                                        "&:hover": { backgroundColor: "#f9fafb", borderColor: "#d1d5db" },
                                    }}
                                >
                                    {loading ? "Redirecting..." : "Manage Subscription"}
                                </Button>
                            )}
                            {isCurrent && currentPlan === "free" && (
                                <div style={{ textAlign: "center", color: "#6b7280", fontSize: "13px", fontWeight: 500, padding: "10px 0" }}>
                                    Your current plan
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Billing;
